import { encodeDeepLink } from "@tg-gallery/deep-links"
import { BookImage, Download, Share2, Trash2 } from "lucide-react"
import { observer } from "mobx-react-lite"
import { useCallback, useEffect, useRef, useState } from "react"
import { mediaUrl, thumbUrl } from "../lib/api.js"
import { getTelegramWebApp } from "../lib/telegram.js"
import { useStore, useT } from "../stores/StoreProvider.js"

interface LightboxProps {
  gallerySlug?: string
  isOwner?: boolean
  galleryId?: string
}

export const Lightbox = observer(function Lightbox({ gallerySlug, isOwner, galleryId }: LightboxProps) {
  const { viewerStore, galleryStore } = useStore()
  const t = useT()
  const touchStartX = useRef(0)
  const [editCaption, setEditCaption] = useState("")

  const currentMedia = galleryStore.detailState.status === "loaded"
    ? galleryStore.detailState.media.find(m => m.id === viewerStore.currentMediaId)
    : null

  // Sync editCaption when current media changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: sync on media id change
  useEffect(() => {
    if (currentMedia) {
      setEditCaption(currentMedia.caption ?? "")
    }
  }, [currentMedia?.id])

  const handleClose = useCallback(() => {
    viewerStore.close()
    getTelegramWebApp()?.HapticFeedback.impactOccurred("light")
  }, [viewerStore])

  const handlePrev = useCallback(() => {
    viewerStore.previous()
    getTelegramWebApp()?.HapticFeedback.selectionChanged()
  }, [viewerStore])

  const handleNext = useCallback(() => {
    viewerStore.next()
    getTelegramWebApp()?.HapticFeedback.selectionChanged()
  }, [viewerStore])

  const handleSharePhoto = useCallback(() => {
    const mediaId = viewerStore.currentMediaId
    if (!mediaId) return
    const botUsername = (import.meta.env.VITE_TELEGRAM_BOT_USERNAME ?? "").replace("@", "")
    const startapp = encodeDeepLink({ type: "view_photo", mediaId })
    const photoLink = `https://t.me/${botUsername}/app?startapp=${startapp}`
    const webapp = getTelegramWebApp()
    if (webapp) {
      webapp.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(photoLink)}&text=${encodeURIComponent(t("lightbox.share_text"))}`)
    } else {
      void navigator.clipboard.writeText(photoLink)
    }
    getTelegramWebApp()?.HapticFeedback.impactOccurred("light")
  }, [viewerStore])

  const handleShareStory = useCallback(() => {
    const mediaId = viewerStore.currentMediaId
    if (!mediaId) return
    const webapp = getTelegramWebApp()
    if (!webapp) return
    const botUsername = (import.meta.env.VITE_TELEGRAM_BOT_USERNAME ?? "").replace("@", "")
    const startapp = encodeDeepLink({ type: "view_photo", mediaId })
    const storyLink = `https://t.me/${botUsername}/app?startapp=${startapp}`
    webapp.shareToStory(mediaUrl(mediaId), {
      widget_link: { url: storyLink, name: t("lightbox.view_in_gallery") },
    })
    getTelegramWebApp()?.HapticFeedback.impactOccurred("medium")
  }, [viewerStore])

  useEffect(() => {
    if (!viewerStore.isOpen) return

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose()
      if (e.key === "ArrowLeft") handlePrev()
      if (e.key === "ArrowRight") handleNext()
    }
    window.addEventListener("keydown", handleKey)
    return () => window.removeEventListener("keydown", handleKey)
  }, [viewerStore.isOpen, handleClose, handlePrev, handleNext])

  const [imgLoaded, setImgLoaded] = useState(false)
  const prevIdRef = useRef<string | null>(null)

  // Reset loading state when media changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on id change
  useEffect(() => {
    if (viewerStore.currentMediaId !== prevIdRef.current) {
      setImgLoaded(false)
      prevIdRef.current = viewerStore.currentMediaId
    }
  }, [viewerStore.currentMediaId])

  if (!viewerStore.isOpen) return null

  const currentId = viewerStore.currentMediaId
  if (!currentId) return null

  return (
    <div
      className="lightbox"
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0]?.clientX ?? 0
      }}
      onTouchEnd={(e) => {
        const endX = e.changedTouches[0]?.clientX ?? 0
        const diff = endX - touchStartX.current
        if (Math.abs(diff) > 50) {
          if (diff > 0) handlePrev()
          else handleNext()
        }
      }}
    >
      <button className="lightbox-close" onClick={handleClose} type="button">
        &times;
      </button>
      {/* Blurred thumbnail placeholder + spinner while full image loads */}
      {!imgLoaded && (
        <div className="lightbox-placeholder">
          <img
            src={thumbUrl(currentId, 400, 400)}
            alt=""
            className="lightbox-placeholder-thumb"
            draggable={false}
          />
          <div className="lightbox-spinner" />
        </div>
      )}
      <img
        key={currentId}
        src={mediaUrl(currentId)}
        alt=""
        draggable={false}
        onLoad={() => setImgLoaded(true)}
        style={{ opacity: imgLoaded ? 1 : 0 }}
      />
      {/* Caption */}
      {currentMedia && (
        <div className="lightbox-caption">
          {isOwner ? (
            <input
              type="text"
              value={editCaption}
              onChange={(e) => setEditCaption(e.target.value)}
              onBlur={() => {
                if (editCaption !== (currentMedia.caption ?? "")) {
                  void galleryStore.updateMediaCaption(currentId, editCaption)
                }
              }}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur() }}
              placeholder={t("lightbox.add_caption")}
              className="bg-transparent text-white text-sm w-full outline-none placeholder:text-white/40"
            />
          ) : currentMedia.caption ? (
            <p className="text-white text-sm">{currentMedia.caption}</p>
          ) : null}
          {currentMedia.uploader && (
            <p className="text-white/50 text-xs mt-1">by {currentMedia.uploader.displayName}</p>
          )}
        </div>
      )}
      <div className="lightbox-actions">
        {isOwner && galleryId && (
          <button
            className="lightbox-action-btn text-red-400"
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              const webapp = getTelegramWebApp()
              const doDelete = () => {
                const mediaIdToDelete = currentId
                const currentTotal = viewerStore.total
                const currentIdx = viewerStore.currentIndex
                void galleryStore.deleteMedia(galleryId, mediaIdToDelete).then((ok) => {
                  if (!ok) return
                  if (currentTotal <= 1) {
                    viewerStore.close()
                  } else {
                    // Update the viewer's media list
                    if (viewerStore.state.status === "open") {
                      const newIds = viewerStore.state.mediaIds.filter((id) => id !== mediaIdToDelete)
                      const newIdx = currentIdx >= newIds.length ? newIds.length - 1 : currentIdx
                      viewerStore.open(newIds, newIdx)
                    }
                  }
                })
              }
              if (webapp) {
                webapp.showConfirm(t("lightbox.delete_confirm"), (ok: boolean) => {
                  if (ok) doDelete()
                })
              } else {
                if (confirm(t("lightbox.delete_confirm"))) doDelete()
              }
            }}
          >
            <Trash2 className="w-5 h-5" />
          </button>
        )}
        <a
          href={`${mediaUrl(currentId)}?download=1`}
          download
          className="lightbox-action-btn"
          onClick={(e) => e.stopPropagation()}
        >
          <Download className="w-5 h-5" />
        </a>
        <button
          className="lightbox-action-btn"
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            handleSharePhoto()
          }}
        >
          <Share2 className="w-5 h-5" />
        </button>
        {getTelegramWebApp() && (
          <button
            className="lightbox-action-btn"
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              handleShareStory()
            }}
          >
            <BookImage className="w-5 h-5" />
          </button>
        )}
      </div>
      <div className="lightbox-counter">
        {viewerStore.currentIndex + 1} / {viewerStore.total}
      </div>
    </div>
  )
})
